'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Menu, X } from 'lucide-react';

type NavbarProps = {
  isAuthenticated?: boolean;
};

export default function Navbar({ isAuthenticated = false }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  // Close on Escape, return focus to toggle button
  useEffect(() => {
    if (!menuOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        toggleButtonRef.current?.focus();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <nav
      aria-label="Main navigation"
      className="fixed top-0 z-50 w-full bg-paper border-b border-rule"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between px-6">
        {/* Wordmark */}
        <Link
          href="/"
          aria-label="Verbal — go to homepage"
          className="font-serif-display text-2xl text-ink"
        >
          Verbal
        </Link>

        {/* Desktop links */}
        <div className="hidden items-center gap-8 md:flex">
          <Link
            href="/how-it-works"
            className="font-sans text-sm text-ink no-underline hover:text-salmon"
          >
            Product
          </Link>
          <Link
            href="/pricing"
            className="font-sans text-sm text-ink no-underline hover:text-salmon"
          >
            Pricing
          </Link>
          <Link
            href="/docs"
            className="font-sans text-sm text-ink no-underline hover:text-salmon"
          >
            Docs
          </Link>
          {!isAuthenticated && (
            <Link
              href="/login"
              className="font-sans text-sm text-ink no-underline hover:text-salmon"
            >
              Log in
            </Link>
          )}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex">
          {isAuthenticated ? (
            <Link
              href="/dashboard"
              className="font-sans text-sm font-medium text-salmon hover:underline"
            >
              Dashboard
            </Link>
          ) : (
            <Link
              href="/signup"
              className="font-sans text-sm font-medium text-salmon hover:underline"
            >
              Get started
            </Link>
          )}
        </div>

        {/* Mobile: hamburger */}
        <button
          ref={toggleButtonRef}
          type="button"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-menu"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex size-9 items-center justify-center text-ink hover:text-salmon focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink md:hidden"
        >
          {menuOpen ? (
            <X size={18} strokeWidth={2} aria-hidden="true" />
          ) : (
            <Menu size={18} strokeWidth={2} aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          id="mobile-nav-menu"
          ref={menuRef}
          className="border-t border-rule bg-paper md:hidden"
        >
          <div className="mx-auto flex max-w-[1100px] flex-col px-6 py-4 gap-1">
            <Link
              href="/how-it-works"
              onClick={closeMenu}
              className="font-sans text-sm text-ink no-underline py-3 hover:text-salmon"
            >
              Product
            </Link>
            <Link
              href="/pricing"
              onClick={closeMenu}
              className="font-sans text-sm text-ink no-underline py-3 hover:text-salmon"
            >
              Pricing
            </Link>
            <Link
              href="/docs"
              onClick={closeMenu}
              className="font-sans text-sm text-ink no-underline py-3 hover:text-salmon"
            >
              Docs
            </Link>
            {isAuthenticated ? (
              <Link
                href="/dashboard"
                onClick={closeMenu}
                className="font-sans text-sm font-medium text-salmon no-underline py-3 hover:underline"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  onClick={closeMenu}
                  className="font-sans text-sm text-ink no-underline py-3 hover:text-salmon"
                >
                  Log in
                </Link>
                <Link
                  href="/signup"
                  onClick={closeMenu}
                  className="font-sans text-sm font-medium text-salmon no-underline py-3 hover:underline"
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
